import PropTypes from 'prop-types';
import React, { Component } from 'react';
import injectSheet from 'react-jss';

import { ENTER } from '~/common/constants/keyCodes';
import { Button } from '~/eds/src/components/Button/Button';
import { Text } from '~/eds/src/components/Text/Text';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { injectIntl } from 'react-intl';
import { testIsDirty } from '~/utils/helper.utils';
import { isValueStrictlyEmpty } from '~/utils/strings';

import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import styles from './NameModal.styles';

class NameModal extends Component {
  constructor(props) {
    super(props);

    this.handleOnKeyboardPress = this.handleOnKeyboardPress.bind(this);
    this.handleActionClick = this.handleActionClick.bind(this);

    this.state = {
      itemName: this.props.itemsCurrentName,
      itemsCurrentDescription: this.props.itemsCurrentDescription,
      bucketInfo: null,
    };

    this.descriptionTextRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleOnKeyboardPress, true);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleOnKeyboardPress, true);
  }

  handleOnKeyboardPress(event) {
    const { itemsCurrentName, itemsCurrentDescription } = this.props;
    const { itemName, itemsCurrentDescription: desciptionValue } = this.state;

    if (event.target === this.descriptionTextRef.current) {
      return;
    }
    const hasChanges =
      !isValueStrictlyEmpty(itemName) &&
      testIsDirty(
        { itemsCurrentName, itemsCurrentDescription },
        {
          itemsCurrentName: itemName,
          itemsCurrentDescription: desciptionValue,
        },
      );
    if (event.keyCode === ENTER && hasChanges) {
      this.handleActionClick();
    }
  }

  handleInputChange(itemName) {
    this.setState({ itemName });
  }

  handleTextAreaChange(itemsCurrentDescription) {
    this.setState({ itemsCurrentDescription });
  }

  handleInputFocus(event) {
    event.target.select();
  }

  handleActionClick() {
    const { itemName, itemsCurrentDescription } = this.state;
    const { itemsParentFolderId, handleNameChange, hideModal, bucketInfo } =
      this.props;

    handleNameChange(itemName, itemsParentFolderId || itemsCurrentDescription);
    if (typeof bucketInfo === 'undefined') hideModal();
  }

  renderErrorMessage() {
    const { classes, errorMessage } = this.props;
    return errorMessage && errorMessage.length ? (
      <div className={classes.modalErrorMessage}>{errorMessage}</div>
    ) : null;
  }

  renderBucketMessage() {
    const { classes, bucketInfo } = this.props;
    return bucketInfo && bucketInfo.length ? (
      <div className={classes.modalBucketMessage}>{bucketInfo}</div>
    ) : null;
  }

  render() {
    const { itemName, itemsCurrentDescription: desciptionValue } = this.state;
    const {
      classes,
      id,
      itemsCurrentName,
      itemsCurrentDescription,
      title,
      labelText,
      includeTextArea,
      labelTextArea,
      confirmButtonText,
      hideModal,
      intl,
      nameMaxLength,
    } = this.props;

    const hasChanges =
      !isValueStrictlyEmpty(itemName) &&
      testIsDirty(
        { itemsCurrentName, itemsCurrentDescription },
        {
          itemsCurrentName: itemName,
          itemsCurrentDescription: desciptionValue,
        },
      );

    const isNameOverLimit =
      nameMaxLength && (itemName?.length ?? 0) > nameMaxLength;

    return (
      <EcModalCard
        id={id}
        title={title}
        content={
          <div>
            <label className={classes.modalInputLabel}>{labelText}</label>
            <input
              className={classes.modalInput}
              value={itemName}
              onChange={(event) => {
                this.handleInputChange(event.target.value);
              }}
              onFocus={(event) => {
                this.handleInputFocus(event);
              }}
              data-testid="name-modal-text-input"
              title={intl.formatMessage(
                {
                  id: 'NameModal.nameInputTitle',
                  defaultMessage: `{title} name input text`,
                  description:
                    'Title for name input field in NameModal. Example: title = "Document Title"',
                },
                { title },
              )}
            />
            {isNameOverLimit && (
              <Text color="status.danger" variant="tiny" role="note">
                {intl.formatMessage(
                  {
                    id: 'NameModal.nameOverMaxLengthError',
                    defaultMessage:
                      '{count}/{maxLength} (Name cannot be longer than {maxLength} characters)',
                    description:
                      'Error message below input when renaming something and the length of the name is over the limit. Example: count = 320',
                  },
                  {
                    count: itemName?.length ?? 0,
                    maxLength: nameMaxLength,
                  },
                )}
              </Text>
            )}
            {includeTextArea && (
              <div className={classes.modalTextAreaContainer}>
                <label className={classes.modalInputLabel}>
                  {labelTextArea}
                </label>
                <textarea
                  ref={this.descriptionTextRef}
                  className={classes.modalTextArea}
                  title={`${title} ${labelTextArea.toLowerCase()} input text`}
                  value={desciptionValue}
                  onChange={(event) => {
                    this.handleTextAreaChange(event.target.value);
                  }}
                />
              </div>
            )}
            {this.renderBucketMessage()}
            {this.renderErrorMessage()}
          </div>
        }
        footer={
          <>
            <Button
              variant="tertiary"
              text={intl.formatMessage({
                id: 'NameModal.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              })}
              onClick={hideModal}
            />
            <Button
              id={OnboardingIdType.CreateDepartmentButton}
              disabled={!hasChanges || isNameOverLimit}
              text={confirmButtonText}
              onClick={this.handleActionClick}
              label="name-modal-confirm-button"
            />
          </>
        }
        hideModal={hideModal}
      />
    );
  }
}

NameModal.propTypes = {
  title: PropTypes.string.isRequired,
  labelText: PropTypes.string,
  id: PropTypes.string,
  itemsParentFolderId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  itemsCurrentName: PropTypes.string,
  itemsCurrentDescription: PropTypes.string,
  confirmButtonIcon: PropTypes.object,
  confirmButtonText: PropTypes.string.isRequired,
  editItemName: PropTypes.func,
  createItemName: PropTypes.func,
  hideModal: PropTypes.func.isRequired,
};

export default injectSheet(styles)(injectIntl(NameModal));
